import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";
import { CheckCircle2, Loader2, SlidersHorizontal, Undo2, X, Truck, ShieldCheck } from "lucide-react";
import BreadCrumb from "Common/BreadCrumb";
import TableContainer from "Common/TableContainer";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import selectStyles from "Common/constants/selectStyles";
import useSearchableOptions from "Common/hooks/useSearchableOptions";
import useCurrentRole from "Common/hooks/useCurrentRole";
import { allowRoles, canFilterByCustomer, canSeeCustomerName, ROLE } from "Common/constants/roles";
import { describePanelSpec, invoiceRowColorClass, notchLabel } from "Common/constants/panelSpec";
import { getItemLabel } from "Common/constants/proformaStatus";
import { apiErrorMessage } from "Common/constants/apiErrors";

const BASE_URL = "https://portal-api.mahanpanel.com";
const UNSETTLED_LAST_WAREHOUSE_ITEM_MESSAGE =
  "این کالا آخرین کالای این سفارش می باشد، باید وضعیت سفارش تسویه شده باشد تا امکان خروج این کالا از انبار وجود داشته باشد.";

const extractList = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

// ProformaInvoiceProductionItemListItem
export interface ProductionItem {
  id: number;
  proforma_invoice_id: number;
  line_number?: number;
  goods_service_id?: number;
  item_status: string;
  goods_code?: string;
  goods_title?: string;
  goods_description?: string | null;
  goods_full_description?: string | null;
  quantity?: number;
  length?: string;
  width?: string;
  unit_price?: string;
  panel_type?: string | null;
  panel_thickness?: number | null;
  top_sheet_material?: string | null;
  bottom_sheet_material?: string | null;
  top_sheet_thickness?: number | null;
  bottom_sheet_thickness?: number | null;
  foam_density?: number | null;
  foam_type?: string | null;
  top_sheet_color?: string | null;
  bottom_sheet_color?: string | null;
  area?: string;
  gross_amount?: string;
  packaging_cost?: string;
  vat_amount?: string;
  net_amount?: string;
  invoice_number?: number;
  customer_id?: number;
  customer_name?: string;
  order_status?: string;
  created_at_jalali?: string;
  updated_at_jalali?: string;
  loading_confirmed?: boolean;
  loading_confirmed_by_id?: number | null;
  loading_confirmed_at?: string | null;
  loading_confirmed_at_jalali?: string | null;
  factory_exit_confirmed?: boolean;
  factory_exit_confirmed_by_id?: number | null;
  factory_exit_confirmed_at?: string | null;
  factory_exit_confirmed_at_jalali?: string | null;
  logistics_legacy?: boolean;
  warehouse_exit_amount?: string | number | null;
  /** کارشناس فروشِ سفارشی که این قلم به آن تعلق دارد (نسخه ۳ بک‌اند). */
  sales_expert_id?: number | null;
  sales_expert_username?: string | null;
  sales_expert_fullname?: string | null;
  /** لحظه‌ی تبدیل پیش‌فاکتور به سفارش فروش (نسخه ISO؛ فقط برای نیازهای داخلی). */
  /** «نک‌دار رو» و «نک‌دار زیر»؛ جایگزین فیلد منسوخ has_notch. */
  has_notch_top?: boolean | null;
  has_notch_bottom?: boolean | null;
  /** منسوخ؛ فقط برای رکوردهای قدیمی. */
  has_notch?: boolean | null;
  order_date?: string | null;
  /** همان لحظه به شمسی، شامل تاریخ و ساعت — همین مقدار نمایش داده می‌شود. */
  order_date_jalali?: string | null;
  [key: string]: any;
}

interface Customer {
  id: number;
  first_name: string;
  last_name: string | null;
}

// کلیدهای فیلتر مشخصات فنی. ترتیب همین آرایه ترتیب نمایش فیلترها در بالای صفحه است.
type SpecFilterKey =
  | "panelType"
  | "thickness"
  | "topSheet"
  | "bottomSheet"
  | "topSheetThickness"
  | "bottomSheetThickness"
  | "topColor"
  | "bottomColor"
  | "foamDensity"
  | "foamType";

const SPEC_FILTERS: { key: SpecFilterKey; label: string }[] = [
  { key: "panelType", label: "نوع پانل" },
  { key: "thickness", label: "ضخامت" },
  { key: "topSheet", label: "نوع ورق رو" },
  { key: "bottomSheet", label: "نوع ورق زیر" },
  { key: "topSheetThickness", label: "ضخامت ورق رو" },
  { key: "bottomSheetThickness", label: "ضخامت ورق زیر" },
  { key: "topColor", label: "رنگ ورق رو" },
  { key: "bottomColor", label: "رنگ ورق زیر" },
  { key: "foamDensity", label: "دانسیته" },
  { key: "foamType", label: "نوع فوم" },
];

// متراژ کل هر قلم = طول × عرض × تعداد (طبق تعریف مستند).
const totalArea = (item: ProductionItem): number =>
  Number(item.length || 0) * Number(item.width || 0) * Number(item.quantity || 0);

export interface ProductionItemsPageProps {
  /** عنوان صفحه */
  pageTitle: string;
  /** مقدار فیلتر item_status این صفحه */
  itemStatus:
    | "sales_order"
    | "in_production"
    | "produced"
    | "in_warehouse"
    | "out_of_warehouse"
    | "loaded"
    | "delivered";
  /** متن دکمه پیشروی، مثلاً «ارسال برای تولید». اگر ندهید دکمه ساخته نمی‌شود. */
  advanceLabel?: string;
  /** متن یا تابع ساخت گفت‌وگوی تأیید پیشروی، دقیقاً مطابق مستند */
  advanceConfirmText?: string | ((item: ProductionItem) => string);
  /** پیام موفقیت پیشروی */
  advanceSuccessText?: string;
  /** آیا دکمه «بازگشت عملیات» نمایش داده شود */
  canRevert?: boolean;
  /** متن خالی بودن فهرست */
  emptyText: string;
  /**
   * نقش‌های غیرادمینی که اجازه‌ی دکمه‌ی پیشروی را دارند. ادمین/سوپرادمین همیشه مجازند.
   * پیشروی و بازگشت جدا تنظیم می‌شوند چون در یک صفحه ممکن است به دو نقش متفاوت تعلق
   * داشته باشند (مثلاً «کالاهای تولید شده»: ورود به انبار برای انباردار، بازگشت برای کارشناس تولید).
   */
  advanceRoles?: readonly number[];
  /** نقش‌های غیرادمینی که اجازه‌ی دکمه‌ی «بازگشت عملیات» را دارند. */
  revertRoles?: readonly number[];
}

const ProductionItemsPage = ({
  pageTitle,
  itemStatus,
  advanceLabel,
  advanceConfirmText,
  advanceSuccessText,
  canRevert = false,
  emptyText,
  advanceRoles = [],
  revertRoles = [],
}: ProductionItemsPageProps) => {
  const navigate = useNavigate();

  // همه‌ی اقلام این وضعیت (بدون فیلتر مشخصات)؛ فیلتر و صفحه‌بندی از روی همین انجام می‌شود.
  const [allItems, setAllItems] = useState<ProductionItem[]>([]);

  // فیلترهای مشخصات فنی، به همان ترتیبی که در مستند خواسته شده.
  const [specFilters, setSpecFilters] = useState<Record<SpecFilterKey, string>>({
    panelType: "",
    thickness: "",
    topSheet: "",
    bottomSheet: "",
    topSheetThickness: "",
    bottomSheetThickness: "",
    topColor: "",
    bottomColor: "",
    foamDensity: "",
    foamType: "",
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<"default" | "asc" | "desc">("default");
  const [reload, setReload] = useState<boolean>(false);

  // شناسه قلمی که در حال ارسال درخواست است، تا دکمه‌ها تا پایان درخواست غیرفعال بمانند
  // و درخواست تکراری ارسال نشود.
  const [busyItemId, setBusyItemId] = useState<number | null>(null);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerId, setCustomerId] = useState<string>("");
  const customerSearch = useSearchableOptions(
    customers,
    (c) => c.first_name + (c.last_name ? ` ${c.last_name}` : ""),
    (c) => String(c.id)
  );

  // هر دکمه نقش‌های مجاز خودش را دارد؛ بقیه نقش‌ها صفحه را فقط‌خواندنی می‌بینند.
  const { roleId } = useCurrentRole();
  const mayAdvance = allowRoles(roleId, advanceRoles);
  const mayRevert = canRevert && allowRoles(roleId, revertRoles);
  const showCustomerName = canSeeCustomerName(roleId);
  const showCustomerFilter = canFilterByCustomer(roleId);
  const changeServerSort = useCallback((nextSortBy: string | undefined, nextSortOrder: "default" | "asc" | "desc") => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setCurrentPage(1);
  }, []);

  // مشخصات فنی هر قلم یک‌بار محاسبه می‌شود تا هم فیلترها و هم ستون‌ها از آن بخوانند.
  const decorated = useMemo(
    () => allItems.map((item) => ({ item, spec: describePanelSpec(item) })),
    [allItems]
  );

  // گزینه‌های هر فیلتر از خودِ داده ساخته می‌شوند تا هرگز با داده واقعی ناهماهنگ نشوند.
  const filterOptions = useMemo(() => {
    const sets: Record<SpecFilterKey, Set<string>> = {
      panelType: new Set(), thickness: new Set(),
      topSheet: new Set(), bottomSheet: new Set(),
      topSheetThickness: new Set(), bottomSheetThickness: new Set(),
      topColor: new Set(), bottomColor: new Set(),
      foamDensity: new Set(), foamType: new Set(),
    };
    decorated.forEach(({ spec }) => {
      SPEC_FILTERS.forEach(({ key }) => {
        const v = (spec as any)[key];
        if (v !== undefined && v !== null && v !== "" && v !== "-") sets[key].add(String(v));
      });
    });
    const out = {} as Record<SpecFilterKey, { value: string; label: string }[]>;
    SPEC_FILTERS.forEach(({ key }) => {
      out[key] = Array.from(sets[key])
        .sort((a, b) => a.localeCompare(b, "fa", { numeric: true }))
        .map((v) => ({ value: v, label: v }));
    });
    return out;
  }, [decorated]);

  // چند فیلتر همزمان با «و» ترکیب می‌شوند: قلم باید همه‌ی شرط‌های انتخاب‌شده را داشته باشد.
  const filteredItems = useMemo(
    () =>
      decorated
        .filter(({ spec }) =>
          SPEC_FILTERS.every(({ key }) => {
            const selected = specFilters[key];
            return !selected || String((spec as any)[key]) === selected;
          })
        )
        .map(({ item }) => item),
    [decorated, specFilters]
  );

  const totalItems = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const pageItems = useMemo(
    () => filteredItems.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredItems, currentPage, pageSize]
  );

  // با تغییر فیلترها به صفحه اول برمی‌گردیم تا صفحه‌ی خالی نمایش داده نشود.
  useEffect(() => {
    setCurrentPage(1);
  }, [specFilters]);

  const activeFilterCount = SPEC_FILTERS.filter(({ key }) => specFilters[key]).length;
  const hasActiveFilter = activeFilterCount > 0;

  // در موبایل فیلترها داخل یک کشو جمع می‌شوند تا بالای صفحه شلوغ نشود.
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);

  // وقتی کشو باز است اسکرول پس‌زمینه قفل می‌شود.
  useEffect(() => {
    if (!filtersOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [filtersOpen]);

  // برچسب مشتری در دو جا (دسکتاپ و کشوی موبایل) استفاده می‌شود.
  const customerLabel = (id: string) => {
    const c = customers.find((x) => String(x.id) === id);
    return c ? c.first_name + (c.last_name ? ` ${c.last_name}` : "") : "";
  };

  const clearFilters = () =>
    setSpecFilters({
      panelType: "", thickness: "",
      topSheet: "", bottomSheet: "",
      topSheetThickness: "", bottomSheetThickness: "",
      topColor: "", bottomColor: "",
      foamDensity: "", foamType: "",
    });

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.get(`${BASE_URL}/customers/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCustomers(extractList(res.data));
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    };
    fetchCustomers();
  }, []);

  // با تغییر فیلتر مشتری باید به صفحه اول برگردیم تا صفحه‌ی خالی نمایش داده نشود.
  useEffect(() => {
    setCurrentPage(1);
  }, [customerId]);

  // فیلترهای مشخصات فنی (نوع/ضخامت/رنگ ورق، دانسیته و نوع فوم) در API وجود ندارند —
  // GET /proforma-invoices/items/ فقط item_status و customer_id را می‌پذیرد. برای اینکه
  // فیلترها با شمارش و صفحه‌بندی درست کار کنند، همه‌ی اقلام این وضعیت یک‌بار گرفته
  // می‌شوند و فیلتر و صفحه‌بندی سمت کلاینت انجام می‌شود.
  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const collected: ProductionItem[] = [];
        let page = 1;
        let pages = 1;
        do {
          const res = await axios.get(`${BASE_URL}/proforma-invoices/items/`, {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              item_status: itemStatus,
              page,
              size: 100, // سقف مجاز سرور
              ...(sortOrder === "default" ? { sort_order: "default" } : { sort_by: sortBy, sort_order: sortOrder }),
              // طبق مستند، برای کارشناس تولید و حراست نباید customer_id ارسال شود.
              ...(showCustomerFilter && customerId ? { customer_id: Number(customerId) } : {}),
            },
          });
          collected.push(...extractList(res.data));
          pages = res.data?.pages || 1;
          page += 1;
        } while (page <= pages && page <= 50); // سقف ایمنی

        setAllItems(collected);
        setErrorMessage("");
      } catch (error: any) {
        console.error("Error fetching production items:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        if (error.response?.status === 403) {
          setErrorMessage("شما مجوز مشاهده این بخش را ندارید.");
          setAllItems([]);
          return;
        }
        setErrorMessage(apiErrorMessage(error, "مشکلی در دریافت فهرست کالاها رخ داده است."));
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, [navigate, itemStatus, customerId, showCustomerFilter, reload, sortBy, sortOrder]);

  const statusChangeErrorMessage = (error: any, fallback: string) => {
    const status = error.response?.status;
    if (status === 409) {
      return (
        apiErrorMessage(error, "وضعیت این کالا تغییر کرده یا این عملیات در وضعیت فعلی مجاز نیست. فهرست را تازه کنید.")
      );
    }
    if (status === 403) return "شما مجوز انجام این عملیات را ندارید.";
    if (status === 404) return "این کالا یافت نشد؛ ممکن است وضعیتش تغییر کرده باشد.";
    // بقیه‌ی خطاها (اعتبارسنجی، تکراری بودن، ...) به فارسی ترجمه می‌شوند.
    return apiErrorMessage(error, fallback);
  };

  // پاسخ موفق سرور مبنا است: فهرست دوباره از سرور خوانده می‌شود و ردیف به‌صورت خودکار
  // از این صفحه خارج می‌شود. در صورت خطا هیچ وضعیت محلی تغییر نمی‌کند.
  const runStatusChange = useCallback(
    async (
      item: ProductionItem,
      action:
        | "advance-status"
        | "revert-status"
        | "confirm-loading"
        | "confirm-factory-exit"
        | "revert-loading"
        | "revert-factory-exit",
      confirmText: string,
      successText: string,
      fallbackError: string
    ) => {
      if (busyItemId !== null) return;

      const confirmed = await Swal.fire({
        title: pageTitle,
        text: confirmText,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "بلی",
        cancelButtonText: "خیر",
      });
      if (!confirmed.isConfirmed) return;

      setBusyItemId(item.id);
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.post(
          `${BASE_URL}/proforma-invoices/${item.proforma_invoice_id}/items/${item.id}/${action}`,
          {},
          { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
        );
        if (res.status === 200) {
          setReload((prev) => !prev);
          Swal.fire("عملیات موفق", successText, "success");
        }
      } catch (error: any) {
        console.error(`${action} error:`, error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        const isUnsettledLastWarehouseItem =
          itemStatus === "in_warehouse" &&
          action === "advance-status" &&
          error.response?.status === 409 &&
          error.response?.data?.detail === UNSETTLED_LAST_WAREHOUSE_ITEM_MESSAGE;
        Swal.fire(
          "خطا",
          isUnsettledLastWarehouseItem
            ? UNSETTLED_LAST_WAREHOUSE_ITEM_MESSAGE
            : statusChangeErrorMessage(error, fallbackError),
          "error"
        );
      } finally {
        setBusyItemId(null);
      }
    },
    [busyItemId, navigate, pageTitle]
  );

  const handleAdvance = useCallback(
    (item: ProductionItem) =>
      runStatusChange(
        item,
        "advance-status",
        typeof advanceConfirmText === "function"
          ? advanceConfirmText(item)
          : advanceConfirmText || "آیا این عملیات را تأیید می‌کنید؟",
        advanceSuccessText || "وضعیت کالا با موفقیت تغییر کرد",
        "خطا در تغییر وضعیت کالا"
      ),
    [runStatusChange, advanceConfirmText, advanceSuccessText]
  );

  const handleRevert = useCallback(
    (item: ProductionItem) =>
      runStatusChange(
        item,
        "revert-status",
        `آیا بازگشت عملیات «${getItemLabel(item)}» به مرحله قبل را تأیید می‌کنید؟`,
        "وضعیت کالا به مرحله قبل بازگشت",
        "خطا در بازگشت عملیات"
      ),
    [runStatusChange]
  );

  const handleLogisticsAction = useCallback(
    (
      item: ProductionItem,
      action: "confirm-loading" | "confirm-factory-exit" | "revert-loading" | "revert-factory-exit"
    ) => {
      const labels = {
        "confirm-loading": ["تأیید بارگیری", "آیا بارگیری این قلم را تأیید می‌کنید؟", "بارگیری تأیید شد"],
        "confirm-factory-exit": ["تأیید خروج از کارخانه", "آیا خروج این قلم از کارخانه را تأیید می‌کنید؟", "خروج از کارخانه تأیید شد"],
        "revert-loading": ["برگشت تأیید بارگیری", "تأیید بارگیری این قلم لغو شود؟", "تأیید بارگیری برگشت داده شد"],
        "revert-factory-exit": ["برگشت تأیید خروج", "تأیید خروج این قلم از کارخانه لغو شود؟", "تأیید خروج از کارخانه برگشت داده شد"],
      } as const;
      const itemDescription = getItemLabel(item);
      const customerName = item.customer_name?.trim() || "-";
      const [, defaultConfirmText, successText] = labels[action];
      const confirmText = action === "confirm-loading"
        ? `آیا اتمام بارگیری کالای «${itemDescription}» برای مشتری «${customerName}» را تأیید می‌کنید؟`
        : action === "confirm-factory-exit"
          ? `آیا خروج از کارخانه کالای «${itemDescription}» برای مشتری «${customerName}» را تأیید می‌کنید؟`
          : defaultConfirmText;
      return runStatusChange(item, action, confirmText, successText, "خطا در تغییر وضعیت لجستیک");
    },
    [runStatusChange]
  );

  // ترتیب ستون‌ها دقیقاً مطابق مستند:
  // کد فاکتور، کد کالا، عنوان کالا، توضیحات کالا، تعداد، طول، عرض، نوع پانل، ضخامت،
  // ورق رو، ورق پشت، ضخامت ورق رو، ضخامت ورق پشت، دانسیته فوم، نوع فوم،
  // رنگ ورق رو، رنگ ورق زیر — و سپس اطلاعات والد سفارش و ستون عملیات.
  const columns = useMemo(
    () => {
      const spec = (cell: any) => describePanelSpec(cell.row.original);
      const num = (v: any, digits = 2) =>
        v === undefined || v === null || v === "" ? "-" : Number(v).toFixed(digits);

      // ترتیب ستون‌ها دقیقاً طبق مستند کارشناس تولید.
      // هر ستون exportValue هم دارد تا خروجی اکسل عیناً همین فیلدها باشد.
      const plain = (v: any) => String(v ?? "-").replace(/[\u2066\u2069]/g, "");
      // نبودِ مقدار با «-» نشان داده می‌شود تا با «ندارد» اشتباه نشود.
      // رکوردهای قدیمی فقط has_notch دارند و همان جایگزین هر دو مقدار می‌شود.
      const notchTop = (it: ProductionItem) => notchLabel(it.has_notch_top ?? it.has_notch);
      const notchBottom = (it: ProductionItem) => notchLabel(it.has_notch_bottom ?? it.has_notch);
      // نام کارشناس فروش: اول نام کامل، بعد نام کاربری.
      const expertName = (it: ProductionItem) =>
        it.sales_expert_fullname || it.sales_expert_username || "-";
      // تاریخ سفارش فقط از order_date_jalali خوانده می‌شود. created_at و updated_at
      // معنای دیگری دارند (ایجاد قلم و آخرین تغییر وضعیت) و نباید جایگزین شوند،
      // وگرنه ویرایش‌های بعدی تاریخ نمایش‌داده‌شده را عوض می‌کنند.
      const orderDate = (it: ProductionItem) => it.order_date_jalali || "-";

      const base: any[] = [
        {
          // طبق مستند، «کارشناس فروش» اولین ستون و «شماره فاکتور» دومین ستون است.
          header: "کارشناس فروش",
          accessorKey: "sales_expert_fullname",
          cell: (c: any) => expertName(c.row.original),
          exportValue: (it: ProductionItem) => expertName(it),
        },
        {
          header: "شماره فاکتور",
          accessorKey: "invoice_number",
          cell: (c: any) => c.getValue() ?? "-",
          exportValue: (it: ProductionItem) => it.invoice_number ?? "-",
        },
        {
          // زمان واقعی تبدیل پیش‌فاکتور به سفارش فروش، مستقیم از سرور.
          header: "تاریخ سفارش",
          accessorKey: "order_date_jalali",
          meta: { serverSortBy: "order_date" },
          cell: (c: any) => orderDate(c.row.original),
          exportValue: (it: ProductionItem) => orderDate(it),
        },
        {
          header: "نوع پانل",
          enableColumnFilter: false,
          cell: (c: any) => spec(c).panelType,
          exportValue: (it: ProductionItem) => plain(describePanelSpec(it).panelType),
        },
        {
          header: "ضخامت",
          enableColumnFilter: false,
          cell: (c: any) => spec(c).thickness,
          exportValue: (it: ProductionItem) => plain(describePanelSpec(it).thickness),
        },
        {
          header: "نک‌دار رو",
          accessorKey: "has_notch_top",
          cell: (c: any) => notchTop(c.row.original),
          exportValue: (it: ProductionItem) => notchTop(it),
        },
        {
          header: "نک‌دار زیر",
          accessorKey: "has_notch_bottom",
          cell: (c: any) => notchBottom(c.row.original),
          exportValue: (it: ProductionItem) => notchBottom(it),
        },
        {
          header: "تعداد",
          accessorKey: "quantity",
          cell: (c: any) => c.getValue() ?? "-",
          exportValue: (it: ProductionItem) => it.quantity ?? "-",
        },
        {
          header: "طول",
          accessorKey: "length",
          cell: (c: any) => num(c.getValue()),
          exportValue: (it: ProductionItem) => num(it.length),
        },
        {
          header: "عرض",
          accessorKey: "width",
          cell: (c: any) => num(c.getValue()),
          exportValue: (it: ProductionItem) => num(it.width),
        },
        {
          // متراژ کل = طول × عرض × تعداد
          header: "متراژ کل",
          enableColumnFilter: false,
          cell: (c: any) => totalArea(c.row.original).toFixed(2),
          exportValue: (it: ProductionItem) => totalArea(it).toFixed(2),
        },
        {
          header: "ورق رو",
          enableColumnFilter: false,
          cell: (c: any) => spec(c).topSheet,
          exportValue: (it: ProductionItem) => plain(describePanelSpec(it).topSheet),
        },
        {
          header: "ورق زیر",
          enableColumnFilter: false,
          cell: (c: any) => spec(c).bottomSheet,
          exportValue: (it: ProductionItem) => plain(describePanelSpec(it).bottomSheet),
        },
        {
          header: "رنگ ورق رو",
          enableColumnFilter: false,
          cell: (c: any) => spec(c).topColor,
          exportValue: (it: ProductionItem) => plain(describePanelSpec(it).topColor),
        },
        {
          header: "رنگ ورق پشت",
          enableColumnFilter: false,
          cell: (c: any) => spec(c).bottomColor,
          exportValue: (it: ProductionItem) => plain(describePanelSpec(it).bottomColor),
        },
        {
          header: "ضخامت ورق رو",
          enableColumnFilter: false,
          cell: (c: any) => spec(c).topSheetThickness,
          exportValue: (it: ProductionItem) => plain(describePanelSpec(it).topSheetThickness),
        },
        {
          header: "ضخامت ورق پشت",
          enableColumnFilter: false,
          cell: (c: any) => spec(c).bottomSheetThickness,
          exportValue: (it: ProductionItem) => plain(describePanelSpec(it).bottomSheetThickness),
        },
        {
          header: "نوع فوم",
          enableColumnFilter: false,
          cell: (c: any) => spec(c).foamType,
          exportValue: (it: ProductionItem) => plain(describePanelSpec(it).foamType),
        },
        {
          header: "دانسیته",
          enableColumnFilter: false,
          cell: (c: any) => spec(c).foamDensity,
          exportValue: (it: ProductionItem) => plain(describePanelSpec(it).foamDensity),
        },
      ];

      // ستون عملیات فقط وقتی ساخته می‌شود که این نقش دست‌کم یکی از دکمه‌ها را داشته باشد.
      const showAdvance = !!advanceLabel && mayAdvance;
      const showRevert = mayRevert;
      const showLoading = itemStatus === "out_of_warehouse" && allowRoles(roleId, [ROLE.LOADING_OPERATOR]);
      const showFactoryExit = itemStatus === "out_of_warehouse" && allowRoles(roleId, [ROLE.SECURITY_GUARD]);

      if (itemStatus === "out_of_warehouse") {
        base.push({
          header: "خروج و بارگیری",
          enableColumnFilter: false,
          cell: (cell: any) => {
            const item: ProductionItem = cell.row.original;
            return (
              <div className="flex flex-col gap-1 text-xs">
                <span className={item.loading_confirmed ? "text-green-600" : "text-slate-500"}>
                  بارگیری: {item.loading_confirmed ? "تأیید شده" : "در انتظار"}
                </span>
                <span className={item.factory_exit_confirmed ? "text-green-600" : "text-slate-500"}>
                  خروج کارخانه: {item.factory_exit_confirmed ? "تأیید شده" : "در انتظار"}
                </span>
                {item.logistics_legacy && <span className="text-slate-400">سابقهٔ نسخهٔ قدیم</span>}
                {item.warehouse_exit_amount != null && (
                  <span>مبلغ خروج: {Number(item.warehouse_exit_amount).toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</span>
                )}
              </div>
            );
          },
          exportable: false,
        });
      }

      if (showAdvance || showRevert || showLoading || showFactoryExit) {
        base.push({
          header: "عملیات",
          enableColumnFilter: false,
          exportable: false,
          cell: (cell: any) => {
            const item: ProductionItem = cell.row.original;
            const busy = busyItemId === item.id;
            return (
              <div className="flex items-center gap-3">
                {showLoading && !item.loading_confirmed && (
                  <button type="button" disabled={busyItemId !== null} onClick={() => handleLogisticsAction(item, "confirm-loading")} className="inline-flex items-center gap-1.5 border border-blue-500 text-blue-600 rounded-lg px-3 py-1.5 text-sm disabled:opacity-50"><Truck className="size-4" />بارگیری</button>
                )}
                {showFactoryExit && !item.factory_exit_confirmed && (
                  <button type="button" disabled={busyItemId !== null} onClick={() => handleLogisticsAction(item, "confirm-factory-exit")} className="inline-flex items-center gap-1.5 border border-indigo-500 text-indigo-600 rounded-lg px-3 py-1.5 text-sm disabled:opacity-50"><ShieldCheck className="size-4" />خروج از کارخانه</button>
                )}
                {itemStatus === "out_of_warehouse" && mayRevert && item.loading_confirmed && (
                  <button type="button" disabled={busyItemId !== null} onClick={() => handleLogisticsAction(item, "revert-loading")} className="text-xs text-orange-600 underline disabled:opacity-50">لغو بارگیری</button>
                )}
                {itemStatus === "out_of_warehouse" && mayRevert && item.factory_exit_confirmed && (
                  <button type="button" disabled={busyItemId !== null} onClick={() => handleLogisticsAction(item, "revert-factory-exit")} className="text-xs text-orange-600 underline disabled:opacity-50">لغو خروج</button>
                )}
                {showAdvance && (
                  <button
                    type="button"
                    disabled={busyItemId !== null}
                    onClick={() => handleAdvance(item)}
                    className="inline-flex items-center gap-1.5 border border-green-500 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 hover:shadow-md hover:shadow-green-200 dark:hover:shadow-green-900/30 hover:-translate-y-0.5 active:translate-y-0 rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={advanceLabel}
                  >
                    {busy ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="size-4" />
                    )}
                    <span>{advanceLabel}</span>
                  </button>
                )}
                {showRevert && (
                  <button
                    type="button"
                    disabled={busyItemId !== null}
                    onClick={() => handleRevert(item)}
                    className="inline-flex items-center gap-1.5 border border-orange-500 text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:shadow-md hover:shadow-orange-200 dark:hover:shadow-orange-900/30 hover:-translate-y-0.5 active:translate-y-0 rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="بازگشت عملیات"
                  >
                    <Undo2 className="size-4" />
                    <span>بازگشت عملیات</span>
                  </button>
                )}
              </div>
            );
          },
        });
      }

      return base;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mayAdvance, mayRevert, showCustomerName, busyItemId, advanceLabel, handleAdvance, handleRevert, handleLogisticsAction, roleId, itemStatus]
  );

  return (
    <React.Fragment>
      <BreadCrumb pageTitle={pageTitle} title="سفارش‌های فروش" />
      {errorMessage && <div className="text-center text-red-500 mb-3">{errorMessage}</div>}

      <div className="card">
        <div className="card-body">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <h6 className="text-15 grow">
              {pageTitle} (<b className="total-Employs">{totalItems}</b>)
            </h6>
          </div>

          {/* فیلترها در همه‌ی اندازه‌ها پشت یک دکمه‌اند تا بالای صفحه شلوغ نشود. */}
          <div className="flex items-center gap-2 mb-4">
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border rounded-lg shadow-sm border-slate-300 dark:border-zink-500 text-slate-700 dark:text-zink-100 hover:bg-slate-50 dark:hover:bg-zink-600"
            >
              <SlidersHorizontal className="size-4 text-custom-500" />
              <span>فیلترها</span>
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 text-xs font-bold text-white rounded-full bg-custom-500">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-orange-500 underline hover:text-orange-700"
              >
                پاک‌کردن
              </button>
            )}
          </div>

          {/* پنجره‌ی فیلتر: وسط صفحه، با عرض واکنش‌گرا و شبکه‌ی ستونی متناسب با اندازه. */}
          {filtersOpen && (
            <div
              className="fixed inset-0 z-[1050] flex items-center justify-center p-4"
              role="dialog"
              aria-modal="true"
            >
              <div
                className="absolute inset-0 bg-slate-900/50"
                onClick={() => setFiltersOpen(false)}
              />
              <div className="relative flex flex-col w-full max-w-3xl overflow-hidden bg-white shadow-2xl dark:bg-zink-700 rounded-2xl max-h-[85vh]">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-zink-500">
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="size-4 text-custom-500" />
                    <h6 className="font-semibold text-15 text-slate-700 dark:text-zink-50">فیلترها</h6>
                    {activeFilterCount > 0 && (
                      <span className="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 text-xs font-bold text-white rounded-full bg-custom-500">
                        {activeFilterCount}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setFiltersOpen(false)}
                    className="p-1 transition-colors rounded-md text-slate-400 hover:text-red-500 hover:bg-slate-100 dark:hover:bg-zink-600"
                    aria-label="بستن"
                  >
                    <X className="size-5" />
                  </button>
                </div>

                <div className="flex-1 px-5 py-4 overflow-y-auto">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {SPEC_FILTERS.map(({ key, label }) => (
                      <div key={key}>
                        <label className="block mb-1 text-xs text-slate-500 dark:text-zink-200">{label}</label>
                        <Select
                          options={filterOptions[key]}
                          value={specFilters[key] ? { value: specFilters[key], label: specFilters[key] } : null}
                          onChange={(opt: any) => setSpecFilters((prev) => ({ ...prev, [key]: opt?.value || "" }))}
                          placeholder={label + "..."}
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          isClearable
                          noOptionsMessage={() => "موردی یافت نشد"}
                        />
                      </div>
                    ))}
                    {showCustomerFilter && (
                      <div>
                        <label className="block mb-1 text-xs text-slate-500 dark:text-zink-200">مشتری</label>
                        <Select
                          options={customerSearch.options}
                          onInputChange={customerSearch.onInputChange}
                          value={
                            customerId
                              ? { value: customerId, label: customerLabel(customerId) }
                              : null
                          }
                          onChange={(opt: any) => setCustomerId(opt?.value || "")}
                          placeholder="فیلتر بر اساس مشتری..."
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          isClearable
                          noOptionsMessage={() => "موردی یافت نشد"}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 px-5 py-4 border-t border-slate-200 dark:border-zink-500">
                  <button
                    type="button"
                    onClick={clearFilters}
                    disabled={!hasActiveFilter}
                    className="px-4 py-2 text-sm font-medium transition-colors border rounded-lg border-slate-300 dark:border-zink-500 text-slate-600 dark:text-zink-100 disabled:opacity-50"
                  >
                    پاک‌کردن
                  </button>
                  <button
                    type="button"
                    onClick={() => setFiltersOpen(false)}
                    className="flex-1 px-4 py-2 text-sm font-bold text-white transition-colors rounded-lg bg-custom-500 hover:bg-custom-600"
                  >
                    نمایش {totalItems} مورد
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-custom-500 border-t-transparent"></div>
            </div>
          ) : pageItems.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns}
              data={pageItems}
              serverSorting={{ sortBy, sortOrder, onChange: changeServerSort }}
              customPageSize={pageSize}
              customnumberfortable={10}
              // همه‌ی اقلام این وضعیت از قبل گرفته شده‌اند؛ خروجی اکسل هم همان
              // فهرست فیلترشده‌ی کامل است، نه فقط صفحه‌ی جاری.
              exportAllRows={async () => filteredItems}
              // همه‌ی ستون‌های مستند باید دیده شوند؛ جدول به‌صورت افقی اسکرول می‌شود.
              // customnumberfortable={columns.length}
              serverPagination={{
                currentPage,
                totalPages,
                onPageChange: setCurrentPage,
                pageSize,
                onPageSizeChange: (size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                },
              }}
              // همه ردیف‌های یک شماره فاکتور یک رنگ می‌گیرند.
              trclassName={(row: ProductionItem) => invoiceRowColorClass(row.invoice_number)}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          ) : (
            <LoadingCounter count={1} text={emptyText} />
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default ProductionItemsPage;
